import * as Sentry from "@sentry/react";
import { lazy, Suspense, useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { AppFooter } from "./components/AppFooter";
import { AuthModal } from "./components/AuthModal";
import { LockScreen } from "./components/LockScreen";
import { MobileBanner } from "./components/MobileBanner";
import { OnboardingGate } from "./components/OnboardingGate";
import { EncryptionProvider } from "./contexts/EncryptionContext";
import { useEncryption } from "./contexts/useEncryption";
import { useCanvasSettings } from "./hooks/useCanvasSettings";
import { useFullLock } from "./hooks/useFullLock";
import { useLockScreen } from "./hooks/useLockScreen";
import { useLogout } from "./hooks/useLogout";
import {
  clearTokens,
  clearWasAuthenticated,
  getAccessToken,
  getEncryptionSalt,
  getIsAdmin,
  getOnboardingFlag,
  getWasAuthenticated,
} from "./lib/api";
import "./components/MobileBanner.css";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import GenogramPage from "./pages/GenogramPage";
import LandingPage from "./pages/LandingPage";
import LearnPage from "./pages/LearnPage";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacyPage from "./pages/PrivacyPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SecurityPage from "./pages/SecurityPage";
import SupportPage from "./pages/SupportPage";
import TourPage from "./pages/TourPage";
import UnlockPage from "./pages/UnlockPage";
import VerificationPendingPage from "./pages/VerificationPendingPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import WaitlistPage from "./pages/WaitlistPage";

const RELOAD_KEY = "traumabomen_chunk_reload";

/** Paths behind authentication; everything else is public or a 404. */
const APP_ROUTE_PREFIXES = ["/unlock", "/trees", "/admin"];

/**
 * Wrap a dynamic import so that a failed chunk load (stale deploy) triggers
 * a single full page reload. Uses sessionStorage to prevent infinite loops.
 */
function lazyWithReload(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY); // privacy-ok: non-sensitive reload flag
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "1"); // privacy-ok
        window.location.reload();
        return new Promise<never>(() => {}); // never resolves; page is reloading
      }
      sessionStorage.removeItem(RELOAD_KEY); // privacy-ok
      return Promise.reject(new Error("Failed to load page after reload"));
    }),
  );
}

const AdminPage = lazyWithReload(() => import("./pages/AdminPage"));
const InsightsPage = lazyWithReload(() => import("./pages/InsightsPage"));
const JournalPage = lazyWithReload(() => import("./pages/JournalPage"));
const PatternPage = lazyWithReload(() => import("./pages/PatternPage"));
const TimelinePage = lazyWithReload(() => import("./pages/TimelinePage"));
// Authenticated, behind AuthGuard. Lazy so a public visitor's first paint does
// not carry the tree list (and its compactId and canvas-stylesheet deps).
const TreeListPage = lazyWithReload(() => import("./pages/TreeListPage"));
const TreeWorkspacePage = lazyWithReload(() => import("./pages/TreeWorkspacePage"));
// Public read-only demo. Lazy so its React Flow canvas stays out of the landing
// entry, exactly like the authenticated workspace.
const DemoTreePage = lazyWithReload(() => import("./pages/DemoTreePage"));

export function ErrorFallback() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "2rem",
        textAlign: "center",
      }}
    >
      <h1 style={{ fontFamily: "var(--font-heading)", marginBottom: "1rem" }}>
        {t("error.title", "Something went wrong")}
      </h1>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "1.5rem" }}>
        {t("error.description", "An unexpected error occurred. Please reload the page.")}
      </p>
      <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
        {t("error.reload", "Reload page")}
      </button>
    </div>
  );
}

export function LazyBoundary({ children }: { children: React.ReactNode }) {
  return <Sentry.ErrorBoundary fallback={<ErrorFallback />}>{children}</Sentry.ErrorBoundary>;
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { masterKey } = useEncryption();
  const hasAccessToken = !!getAccessToken();
  if (!hasAccessToken && !getWasAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  // Locked or session expired: the auth modal is asking for the passphrase or
  // credentials. Nothing of the tree may render behind it, not even dimmed.
  if (!hasAccessToken || !masterKey) return null;
  return <>{children}</>;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  if (!getAccessToken() || !getIsAdmin()) {
    return <Navigate to="/trees" replace />;
  }
  return <>{children}</>;
}

export function OnboardingGuard({
  children,
  onStartDemo,
  onLogout,
}: {
  children: React.ReactNode;
  onStartDemo?: () => void;
  onLogout?: () => void;
}) {
  const { masterKey } = useEncryption();
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const isAuthenticated = !!getAccessToken();

  // Read the flag on every render instead of caching it in state. login() and
  // register() rewrite it after this guard has mounted, in both directions: a
  // cached "acknowledged" from an earlier account in the same tab would let a
  // newly registered account skip the safety gate. Unlocking sets masterKey,
  // which re-renders this guard after either call.
  const acknowledged = getOnboardingFlag();

  if (isAuthenticated && masterKey && !acknowledged) {
    return (
      <OnboardingGate onAcknowledged={rerender} onStartDemo={onStartDemo} onLogout={onLogout} />
    );
  }

  return <>{children}</>;
}

function AppContent() {
  const {
    masterKey,
    verifyPassphrase,
    setMasterKey,
    setPassphraseHash,
    setTreeKeys,
    setKeyRingBase64,
    setIsMigrated,
  } = useEncryption();

  const [authSalt, setAuthSalt] = useState<string | null>(null);
  const [authHint, setAuthHint] = useState<string | null>(null);

  const hasAccessToken = !!getAccessToken();
  const wasAuth = getWasAuthenticated();
  const { pathname } = useLocation();

  // Only these need a session. Inverted rather than listing the public pages,
  // because the list has to cover unknown paths too: those render the 404, and
  // asking someone to log in to see a page that does not exist is nonsense.
  // It also means a new public page needs no edit here.
  const isPublicRoute = !APP_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  // Determine auth modal mode (skip on public routes)
  let authModalMode: "unlock" | "reauth" | null = null;
  if (!isPublicRoute) {
    if (hasAccessToken && !masterKey) {
      authModalMode = "unlock";
    } else if (!hasAccessToken && wasAuth) {
      authModalMode = "reauth";
    }
  }

  // Fetch salt when in unlock mode
  useEffect(() => {
    if (authModalMode !== "unlock") return;
    let cancelled = false;
    getEncryptionSalt()
      .then((res) => {
        if (!cancelled) {
          setAuthSalt(res.encryption_salt);
          setAuthHint(res.passphrase_hint);
        }
      })
      .catch(() => {
        // Token invalid, switch to re-auth
        if (!cancelled) clearTokens();
      });
    return () => {
      cancelled = true;
    };
  }, [authModalMode]);

  const handleFullLock = useFullLock();

  const { settings: canvasSettings } = useCanvasSettings();
  const fullTimeoutMs =
    canvasSettings.autoLockMinutes > 0
      ? canvasSettings.autoLockMinutes * 60 * 1000
      : Number.MAX_SAFE_INTEGER;

  const { lockLevel, wrongAttempts, lock, unlock, failedAttempt } = useLockScreen({
    enabled: masterKey !== null,
    fullTimeoutMs,
    onFullLock: handleFullLock,
  });

  const handleLogout = useLogout();
  const navigate = useNavigate();
  const handleStartDemo = useCallback(() => navigate("/trees?start=demo"), [navigate]);

  const handleLockUnlock = useCallback(
    async (passphrase: string) => {
      const valid = await verifyPassphrase(passphrase);
      if (valid) {
        unlock();
      } else {
        failedAttempt();
      }
    },
    [verifyPassphrase, unlock, failedAttempt],
  );

  function handleAuthModalSuccess(result: {
    masterKey: CryptoKey;
    passphraseHash: string;
    treeKeys: Map<string, CryptoKey>;
    keyRingBase64: Map<string, string>;
  }) {
    setMasterKey(result.masterKey);
    setPassphraseHash(result.passphraseHash);
    setTreeKeys(result.treeKeys);
    setKeyRingBase64(result.keyRingBase64);
    setIsMigrated(true);
    setAuthSalt(null);
    setAuthHint(null);
  }

  function handleAuthModalLogout() {
    clearWasAuthenticated();
    handleLogout();
  }

  return (
    <OnboardingGuard onStartDemo={handleStartDemo} onLogout={handleLogout}>
      {authModalMode && (
        <AuthModal
          mode={authModalMode}
          hint={authHint}
          salt={authSalt}
          onUnlockSuccess={handleAuthModalSuccess}
          onReauthSuccess={handleAuthModalSuccess}
          onLogout={handleAuthModalLogout}
        />
      )}
      {lockLevel === "blur" && (
        <LockScreen
          wrongAttempts={wrongAttempts}
          onUnlock={handleLockUnlock}
          onLogout={handleLogout}
        />
      )}
      <div className="app-layout">
        <MobileBanner />
        <main className="app-main">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/learn" element={<LearnPage />} />
              <Route path="/genogram" element={<GenogramPage lang="en" />} />
              <Route path="/genogram-maken" element={<GenogramPage lang="nl" />} />
              <Route path="/tour" element={<TourPage />} />
              <Route
                path="/demo"
                element={
                  <LazyBoundary>
                    <DemoTreePage />
                  </LazyBoundary>
                }
              />
              <Route path="/verify-pending" element={<VerificationPendingPage />} />
              <Route path="/verify" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/unlock" element={<UnlockPage />} />
              <Route
                path="/trees"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <TreeListPage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/trees/:id"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <TreeWorkspacePage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/trees/:id/timeline"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <TimelinePage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/trees/:id/patterns"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <PatternPage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/trees/:id/journal"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <JournalPage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/trees/:id/insights"
                element={
                  <AuthGuard>
                    <LazyBoundary>
                      <InsightsPage />
                    </LazyBoundary>
                  </AuthGuard>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <LazyBoundary>
                      <AdminPage />
                    </LazyBoundary>
                  </AdminGuard>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        <AppFooter onLock={masterKey ? lock : undefined} />
      </div>
    </OnboardingGuard>
  );
}

export default function App() {
  // Clear the chunk-reload flag on successful app boot so future deploys can retry.
  sessionStorage.removeItem(RELOAD_KEY); // privacy-ok

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <EncryptionProvider>
        <AppContent />
      </EncryptionProvider>
    </Sentry.ErrorBoundary>
  );
}
